import React from 'react'
import AutoComplete from 'components/forms/combobox/AutoComplete'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import uuid from 'uuid/v4'
import { DISPLAY } from 'constants/display'
import { EXTERNAL_ENTITY } from 'constants/entities'
import 'scss/5_components/_autocomplete.scss'

const debounceAmount = 25

export const sortArrBy = (arr, sortOrder) => {
  if (!Array.isArray(sortOrder)) return []
  return arr.sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type))
}

/** @deprecated Create an abstraction of <AutocompleteBase /> in Transfix UI instead */
export class Autocomplete extends React.PureComponent {
  constructor(props) {
    super(props)
    const { value } = props
    this.state = {
      suggestions: [],
      value: value || '',
      fullSelection: {},
      isLoading: false,
    }
    this.loadSuggestions = debounce(this.loadSuggestions, debounceAmount)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps = ({ value }) => {
    if (!value) {
      this.setState({ suggestions: [], value: '', fullSelection: {} })
    } else if (value && value !== this.state.value) {
      this.setState({ suggestions: [], value })
    }
  }

  onChange = (e, { newValue }) => {
    if (e) e.preventDefault()
    if (!newValue || newValue.length < 1) return this.props.onChange({ value: '' })
    if (newValue === DISPLAY.noResults) return this.setState({ suggestions: [] })
    return this.setState({ value: newValue })
  }

  onSuggestionsFetchRequested = ({ value }) => {
    this.onChange(null, { newValue: value })

    if (value === '') {
      this.onSuggestionsClearRequested()
    } else {
      this.loadSuggestions(value)
    }
  }

  onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] })
  }

  getSuggestionValue = selectedOption => {
    const suggestion = this.state.suggestions.find(({ id }) => id === selectedOption.id) || {}
    const { name = '' } = suggestion
    this.setState(
      prevState => ({
        value: name,
        fullSelection: prevState.suggestions.find(({ id }) => id === selectedOption.id) || {},
      }),
      () =>
        this.props.onChange({
          value: suggestion.id,
          fullSelection: suggestion,
          selected: true,
        })
    )

    return name
  }

  autoCompleteParams = searchTerm => {
    const { companyId, type: searchType } = this.props
    // if the companyId is invalid, like if it's a string or null, we should not submit it to the back-end
    const id = Number(companyId) || null
    const type = id ? searchType.toLowerCase() : searchType.replace(/(_users)/gim, '').toLowerCase()

    if (type === 'carrier_activated') {
      return [{ type: 'carrier', state: 'activated' }]
    }
    if (type === 'carrier') {
      return [{ type }]
    }
    if (['mc', 'dot'].includes(type)) {
      if (parseInt(searchTerm, 10)) {
        return [{ type: 'carrier' }]
      }
      return [{ type: 'carrier' }]
    }
    if (type === 'shipper_confirmed') {
      return [{ type: 'shipper', state: 'confirmed' }]
    }
    if (type === 'shipper') {
      return [{ type }]
    }
    if (type === 'user') {
      return [{ type }]
    }
    if (type === 'transfix_admin') {
      return [{ type }]
    }
    if (type === 'shipper_users') {
      return [{ type, shipper_application_id: String(id) }]
    }
    if (['carrier_users', 'dispatcher', 'driver'].includes(type)) {
      return [{ type, carrier_application_id: String(id) }]
    }
    if (type === '!user') {
      return [{ type: 'carrier' }, { type: 'shipper' }]
    }
    if (type === '!shipper') {
      return [{ type: 'carrier' }, { type: 'user' }]
    }
    if (type === '!carrier') {
      return [{ type: 'shipper' }, { type: 'user' }]
    }
    if (type === 'factor') {
      return [{ type }]
    }
    if (type === 'dat_market') {
      return [{ type }]
    }
    return [{ type: 'carrier' }, { type: 'shipper' }, { type: 'user' }]
  }

  loadSuggestions = async (searchTerm = '') => {
    const isSearchingExternal = searchTerm.includes('Search FMCSA for')
    if (isSearchingExternal) return
    try {
      this.setState({ value: searchTerm, suggestions: [], isLoading: true })
      const searchOptions = this.autoCompleteParams(searchTerm)
      const requestData = {
        endpoint: ENDPOINTS.search,
        params: {
          perPage: 10,
          filters: {
            searchOptions,
            searchTerm: `*${searchTerm}*`,
          },
        },
      }
      const results = await callApi(requestData)
      const { value } = this.state
      if (value === searchTerm) {
        this.formatSuggestions(results)
      }
      this.setState({ isLoading: false })
    } catch (error) {
      this.setState(
        { value: searchTerm, isLoading: false },
        this.props.onChange({ value: searchTerm })
      )
    }
  }

  formatSuggestions = (suggestions = {}) => {
    let sortedNamesearch
    const { renderOrder, showDefaultOption } = this.props
    const { value } = this.state
    let { namesearch = [] } = suggestions
    const results = namesearch && namesearch.length > 0
    const isCurrentlyExternal =
      this.state.fullSelection && this.state.fullSelection.id === EXTERNAL_ENTITY.id
    const externalEntityName = isCurrentlyExternal
      ? this.state.value
      : `Search FMCSA for ${value}...`
    const externalSuggestion = { ...EXTERNAL_ENTITY, name: externalEntityName, original: value }

    namesearch = namesearch.map(item => {
      if (item.id && (!item.name || !item.name.length)) item.name = `${item.id}`
      return item
    })

    if (renderOrder && results) {
      sortedNamesearch = sortArrBy(namesearch, renderOrder)
    } else {
      sortedNamesearch = namesearch
    }

    if (showDefaultOption && !isCurrentlyExternal) {
      sortedNamesearch.push(externalSuggestion)
    }
    const lastSuggestion =
      showDefaultOption && !isCurrentlyExternal && this.state.value.length
        ? externalSuggestion
        : { name: DISPLAY.noResults }
    if (!results) return this.setState({ suggestions: [lastSuggestion] })
    return this.setState(prevState => ({ suggestions: sortedNamesearch || prevState.suggestions }))
  }

  formatEntry(entry) {
    const { value } = this.state
    let text = entry.name

    if (entry.type === 'carrier') {
      const regex = new RegExp(value, 'gi')
      let matchedMeta = `DOT ${entry.dotNumber}` // default

      if (regex.test(entry.mcNumber)) {
        matchedMeta = `MC ${entry.mcNumber}`
      } else if (regex.test(entry.dotNumber)) {
        matchedMeta = `DOT ${entry.dotNumber}`
      } else if (regex.test(String(entry.id))) {
        matchedMeta = `ID ${entry.id}`
      }

      text = `${text} · ${matchedMeta}`
    }

    return { text, id: entry.id }
  }

  render() {
    const { isLoading, suggestions, value } = this.state
    const {
      id = uuid(),
      placeholder,
      dataTest,
      label = '',
      required,
      disabled,
      type,
      validationMessage,
      large,
    } = this.props
    return (
      <AutoComplete
        id={id}
        disabled={disabled}
        isLoading={isLoading}
        label={label}
        onChange={this.onSuggestionsFetchRequested}
        onOptionSelection={this.getSuggestionValue}
        value={value}
        placeholder={placeholder}
        required={required}
        suggestions={suggestions.map(suggestion => this.formatEntry(suggestion))}
        dataTest={dataTest}
        type={type}
        error={validationMessage}
        large={large}
      />
    )
  }
}

Autocomplete.propTypes = {
  dataTest: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  showDefaultOption: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  renderOrder: PropTypes.arrayOf(PropTypes.string),
  validationMessage: PropTypes.string,
  large: PropTypes.bool,
}

Autocomplete.defaultProps = {
  onChange: () => {},
  dataTest: 'autocomplete-search',
  id: undefined,
  label: '',
  value: '',
  type: '',
  companyId: null,
  placeholder: 'Search',
  showDefaultOption: false,
  required: false,
  disabled: false,
  renderOrder: null,
  validationMessage: '',
  large: false,
}
